/**
 * @class MegaMenu
 */
class MegaMenu extends HTMLElement {
  connectedCallback() {
    this.options = {
      ...this.options,
      ...JSON.parse(this.dataset.options || "{}"),
    };

    this.triggers = this.querySelectorAll("[data-mega-menu-trigger]");
    this.dropdowns = this.querySelectorAll("[data-mega-menu-target]");
    this.hoverTimeout = null;
    this.isTouchDevice = this.detectTouchDevice();
    this.boundEvents = new Map();

    this.attachEventListeners();
    this.setupTouchModeDetection();

    document.addEventListener("click", (e) => this.closeWhenClickOutside(e));
  }

  toggleDropdown(e) {
    e.stopPropagation();
    e.preventDefault();
    const dropdownTarget = e.currentTarget.getAttribute("data-dropdown-target");
    const dropdown = document.getElementById(dropdownTarget);

    if (dropdown?.classList?.contains("show")) {
      dropdown.classList.remove("show");
      document.body.classList.remove("desktop-navigation-hover-active");
    } else {
      this.closeAllDropdowns();
      dropdown?.classList?.add("show");
      document.body.classList.add("desktop-navigation-hover-active");
    }
  }

  closeAllDropdowns() {
    this.dropdowns.forEach((dropdown) => {
      dropdown.classList.remove("show");
    });
  }

  handleMouseEnter(e) {
    this.clearHoverTimeout();
    const dropdownTarget = e.currentTarget.getAttribute("data-dropdown-target");
    const dropdown = document.getElementById(dropdownTarget);
    
    this.closeAllDropdowns();
    dropdown?.classList?.add("show");
    document.body.classList.add("desktop-navigation-hover-active");
  }

  handleMouseLeave(e) {
    this.hoverTimeout = setTimeout(() => {
      this.closeAllDropdowns();
      document.body.classList.remove("desktop-navigation-hover-active");
    }, 300);
  }

  handleDropdownMouseLeave(e) {
    this.hoverTimeout = setTimeout(() => {
      this.closeAllDropdowns();
      document.body.classList.remove("desktop-navigation-hover-active");
    }, 300);
  }

  clearHoverTimeout() {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
  }

  detectTouchDevice() {
    return (('ontouchstart' in window) ||
            (navigator.maxTouchPoints > 0) ||
            (navigator.msMaxTouchPoints > 0));
  }

  attachEventListeners() {
    this.removeEventListeners();

    this.triggers.forEach((trigger, index) => {
      if (this.isTouchDevice) {
        const clickHandler = (e) => this.toggleDropdown(e);
        trigger.addEventListener("click", clickHandler);
        this.boundEvents.set(`trigger-${index}`, { element: trigger, events: { click: clickHandler } });
      } else {
        const mouseEnterHandler = (e) => this.handleMouseEnter(e);
        const mouseLeaveHandler = (e) => this.handleMouseLeave(e);
        trigger.addEventListener("mouseenter", mouseEnterHandler);
        trigger.addEventListener("mouseleave", mouseLeaveHandler);
        this.boundEvents.set(`trigger-${index}`, { 
          element: trigger, 
          events: { 
            mouseenter: mouseEnterHandler, 
            mouseleave: mouseLeaveHandler 
          }
        });
      }
    });

    if (!this.isTouchDevice) {
      this.dropdowns.forEach((dropdown, index) => {
        const mouseEnterHandler = () => this.clearHoverTimeout();
        const mouseLeaveHandler = (e) => this.handleDropdownMouseLeave(e);
        dropdown.addEventListener("mouseenter", mouseEnterHandler);
        dropdown.addEventListener("mouseleave", mouseLeaveHandler);
        this.boundEvents.set(`dropdown-${index}`, {
          element: dropdown,
          events: {
            mouseenter: mouseEnterHandler,
            mouseleave: mouseLeaveHandler
          }
        });
      });
    }
  }

  removeEventListeners() {
    this.boundEvents.forEach((data) => {
      const { element, events } = data;
      Object.keys(events).forEach(eventType => {
        element.removeEventListener(eventType, events[eventType]);
      });
    });
    this.boundEvents.clear();
  }

  setupTouchModeDetection() {
    let mouseMoveTimer;
    
    // Detect touch start events to switch to touch mode
    document.addEventListener('touchstart', () => {
      if (!this.isTouchDevice) {
        this.isTouchDevice = true;
        this.attachEventListeners();
      }
    }, { once: false, passive: true });

    // Detect mouse events to switch to mouse mode (with debouncing)
    document.addEventListener('mousemove', () => {
      clearTimeout(mouseMoveTimer);
      mouseMoveTimer = setTimeout(() => {
        if (this.isTouchDevice) {
          this.isTouchDevice = false;
          this.attachEventListeners();
        }
      }, 100);
    }, { once: false, passive: true });

    // Listen for resize events that might indicate device orientation change
    window.addEventListener('resize', () => {
      setTimeout(() => {
        const newTouchDevice = this.detectTouchDevice();
        if (newTouchDevice !== this.isTouchDevice) {
          this.isTouchDevice = newTouchDevice;
          this.attachEventListeners();
        }
      }, 100);
    });
  }

  closeWhenClickOutside(event) {
    if (!event.target.closest("mega-menu")) {
      this.dropdowns.forEach((dropdown) => {
        dropdown.classList.remove("show");
        document.body.classList.remove("desktop-navigation-hover-active");
      });
    }
  }
}

if (!customElements.get("mega-menu")) {
  customElements.define("mega-menu", MegaMenu);
}
